import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Message, Placeholder } from 'rsuite';
import { Button, ConfirmModal, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { showToast as showExpenseToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import ExpenseCardList from '../components/expenses/ExpenseCardList';
import ExpenseFilters, { EMPTY_EXPENSE_FILTERS, filterExpenses } from '../components/expenses/ExpenseFilters';
import ExpenseForm from '../components/expenses/ExpenseForm';
import ExpenseGroupModal from '../components/expenses/ExpenseGroupModal';
import ExpenseGroupNextStepModal from '../components/expenses/ExpenseGroupNextStepModal';
import ExpenseHelpModal from '../components/expenses/ExpenseHelpModal';
import ExpenseMoveToGroupModal from '../components/expenses/ExpenseMoveToGroupModal';
import ExpenseTypeModal from '../components/expenses/ExpenseTypeModal';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { getFriendlyUploadError } from '../utils/expenseAttachments';
import { EXPENSE_STATUS, isDraftExpense, isOpenExpense } from '../utils/expenseStatus';
import { buildExpenseGroups } from '../utils/expenseGroups';
import {
  useCancelExpense,
  useCreateExpense,
  useCreateExpenseGroup,
  useExpenses,
  useMoveExpenseToGroup,
  useRenameExpenseGroup,
  useSubmitExpense,
  useSubmitExpenseGroup,
  useUpdateExpense,
  useUploadExpenseAttachment,
} from '../hooks/useQueries';

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const countExpenses = (view) => (
  view.ungrouped.length
  + view.groups.reduce((total, group) => total + group.expenses.length, 0)
);

export default function ExpensePage() {
  const auth = useAuth();
  const currentEmployeeId = auth.employee?.id || null;
  const expensesQuery = useExpenses({ mine: true, enabled: true });
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const cancelExpense = useCancelExpense();
  const uploadExpenseAttachment = useUploadExpenseAttachment();
  const createExpenseGroup = useCreateExpenseGroup();
  const submitExpenseGroup = useSubmitExpenseGroup();
  const moveExpenseToGroup = useMoveExpenseToGroup();
  const renameExpenseGroup = useRenameExpenseGroup();
  const submitExpense = useSubmitExpense();

  // Group flow: choosing "part of a group" creates the group up front, then the
  // create modal stays pointed at it until the employee says they're done.
  const [typeChooserOpen, setTypeChooserOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupSavedCount, setGroupSavedCount] = useState(0);
  const [nextStepOpen, setNextStepOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);

  // The expense whose group is being changed, if the dialog is open.
  const [moveTarget, setMoveTarget] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteExpenseTarget, setDeleteExpenseTarget] = useState(null);
  const [createDirty, setCreateDirty] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  // Held while the New Expense dialog fades out, after the batch may already be cleared.
  const createTitle = useLastPresent(
    createOpen ? (activeGroup ? `New Expense — ${activeGroup.name}` : 'New Expense') : null
  ) || 'New Expense';
  const editSubmitRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateMatch = (eventOrList) => {
      setIsMobileViewport(Boolean(eventOrList.matches));
    };

    updateMatch(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateMatch);
      return () => mediaQuery.removeEventListener('change', updateMatch);
    }

    mediaQuery.addListener(updateMatch);
    return () => mediaQuery.removeListener(updateMatch);
  }, []);

  const allExpenses = useMemo(() => expensesQuery.data || [], [expensesQuery.data]);
  const [filters, setFilters] = useState(EMPTY_EXPENSE_FILTERS);
  const shownExpenses = useMemo(() => filterExpenses(allExpenses, filters), [allExpenses, filters]);
  const nonApprovedExpenses = useMemo(
    () => shownExpenses.filter((expense) => String(expense.status || '').toLowerCase() !== 'approved'),
    [shownExpenses]
  );
  const approvedExpenses = useMemo(
    () => shownExpenses.filter((expense) => String(expense.status || '').toLowerCase() === 'approved'),
    [shownExpenses]
  );
  // Totals follow the filters, and stay over every shown expense, grouped or not — collapsing a batch into one
  // card shouldn't change what the employee is owed. They do exclude expenses
  // filed for someone else, which this list also carries: those are owed to that
  // employee, not to whoever entered them.
  const isOwnExpense = useCallback(
    (expense) => !currentEmployeeId || String(expense.employee_id) === String(currentEmployeeId),
    [currentEmployeeId]
  );
  // Drafts are left out as well: nothing has been claimed for one yet, so
  // counting it as money awaiting reimbursement would overstate what is owed.
  const pendingTotal = useMemo(
    () => nonApprovedExpenses
      .filter((expense) => isOwnExpense(expense) && !isDraftExpense(expense))
      .reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0),
    [nonApprovedExpenses, isOwnExpense]
  );
  const approvedTotal = useMemo(
    () => approvedExpenses.filter(isOwnExpense).reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0),
    [approvedExpenses, isOwnExpense]
  );

  // Grouping is done once, over every expense, and the panels divide the result
  // afterwards. Building it separately per panel would split a batch whose
  // expenses aren't all at the same stage — a common state while approvals are
  // working through one — into two cards under the same name, each reporting a
  // count, a total and a progress line drawn from only the half it could see.
  // The panels group only what the filters let through; the open-batch dialog
  // and its helpers keep the full grouping so a batch opens whole.
  const groupedView = useMemo(() => buildExpenseGroups(allExpenses), [allExpenses]);
  const shownView = useMemo(() => buildExpenseGroups(shownExpenses), [shownExpenses]);

  const isApproved = (expense) => String(expense?.status || '').toLowerCase() === 'approved';

  // A batch is done with only when nothing in it is outstanding; until then it
  // belongs with the pending work, whatever some of its expenses have already
  // done. Its card still summarises the whole batch, which is what the status
  // line under the name is there to explain.
  const pendingView = useMemo(
    () => ({
      groups: shownView.groups.filter((group) => !group.expenses.every(isApproved)),
      ungrouped: shownView.ungrouped.filter((expense) => !isApproved(expense)),
    }),
    [shownView]
  );
  const approvedView = useMemo(
    () => ({
      groups: shownView.groups.filter((group) => group.expenses.every(isApproved)),
      ungrouped: shownView.ungrouped.filter(isApproved),
    }),
    [shownView]
  );

  // A batch shows as one card but is several expenses; the bubble counts what
  // the section holds, so it agrees with the total sitting next to it.
  const pendingCount = useMemo(() => countExpenses(pendingView), [pendingView]);
  const approvedCount = useMemo(() => countExpenses(approvedView), [approvedView]);

  // The open group is looked up from the live list rather than held in state, so
  // it stays current as expenses are added, submitted or deleted underneath it.
  const openGroup = useMemo(
    () => (openGroupId
      ? groupedView.groups.find((group) => String(group.id) === String(openGroupId)) || null
      : null),
    [openGroupId, groupedView]
  );

  // What the open group could take in: anything of this employee's that isn't
  // already in a batch and is still theirs to move. The server allows the move
  // only while nobody has decided the expense — PATCH /expenses/:id/group
  // refuses anything else — so an approved or rejected one is never offered
  // rather than offered and then refused.
  const addableExpenses = useMemo(() => {
    if (!openGroup) return [];

    return groupedView.ungrouped.filter((expense) => {
      if (!isOpenExpense(expense)) return false;
      // A batch belongs to one employee and the API rejects a mismatch.
      if (openGroup.employeeId && String(expense.employee_id) !== String(openGroup.employeeId)) {
        return false;
      }
      return true;
    });
  }, [openGroup, groupedView.ungrouped]);

  const submitCreate = async (submission) => {
    const basePayload = submission?.payload || submission;
    // An expense filed into a batch is saved, not claimed: the batch goes for
    // approval as a whole, and submitting it promotes everything inside. So it
    // starts unsubmitted whichever button was pressed, and says so on its card
    // until the batch is sent.
    const payload = activeGroup
      ? { ...basePayload, group_id: activeGroup.id, status: EXPENSE_STATUS.DRAFT }
      : basePayload;
    const attachments = submission?.attachments || [];

    let created;
    try {
      created = await createExpense.mutateAsync(payload);
    } catch (err) {
      showExpenseToast('error', String(err?.message || err || 'Unable to submit expense'));
      throw err;
    }

    // Past this point the expense exists. A receipt that won't upload — most
    // often because the employee's document library was never set up — is
    // therefore a problem with the receipt, not with the expense, and treating
    // it as a failed submit would leave the form open over a saved expense and
    // invite a duplicate. Report it as the partial success it is and move on.
    try {
      // Uploads are independent of each other and each one is a SharePoint
      // round trip, so run them together rather than paying that latency once
      // per receipt.
      await Promise.all(
        attachments.map(({ file, isReceipt, receiptAmount }) => uploadExpenseAttachment.mutateAsync({
          expenseId: created?.id,
          file,
          isReceipt,
          receiptAmount,
        }))
      );
    } catch (err) {
      const reason = getFriendlyUploadError(err);
      showExpenseToast(
        'warning',
        `Your expense was saved, but the receipt couldn't be attached: ${reason}`,
        { duration: 12000 }
      );
      // The modal is about to close over this mutation's error state; clear it
      // so the next expense doesn't open with the last one's failure showing.
      uploadExpenseAttachment.reset();
    }

    setCreateDirty(false);
    setCreateOpen(false);

    // In group mode the expense is saved, not submitted, so hand straight back
    // to the "another one?" prompt instead of returning to the list.
    if (activeGroup) {
      setGroupSavedCount((count) => count + 1);
      setNextStepOpen(true);
    }
  };

  const shouldDiscardUnsavedChanges = () => {
    if (typeof window === 'undefined') return true;
    return window.confirm('You have unsaved changes. Discard them?');
  };

  const requestCloseCreate = () => {
    if (createDirty && !shouldDiscardUnsavedChanges()) return;
    setCreateDirty(false);
    setCreateOpen(false);
    // Backing out of the form mid-batch returns to the group rather than
    // dropping the employee back on the list with the batch half-built.
    if (activeGroup) setNextStepOpen(true);
  };

  const startNewExpense = () => {
    setActiveGroup(null);
    setGroupSavedCount(0);
    setTypeChooserOpen(true);
  };

  const startIndividualExpense = () => {
    setActiveGroup(null);
    setTypeChooserOpen(false);
    setCreateDirty(false);
    setCreateOpen(true);
  };

  const startGroupedExpense = async (name, employeeId) => {
    try {
      const group = await createExpenseGroup.mutateAsync({ name, employee_id: employeeId });
      // The owner travels with the group: every expense filed into it has to
      // belong to the same employee, so the form is pinned to them rather than
      // letting the two disagree.
      setActiveGroup({ id: group.id, name: group.name, employeeId: group.employee_id });
      setGroupSavedCount(0);
      setTypeChooserOpen(false);
      setCreateDirty(false);
      setCreateOpen(true);
    } catch (err) {
      showExpenseToast('error', String(err?.message || err || 'Unable to start the expense group'));
    }
  };

  // "Submit another expense" from the batch prompt, and the group modal's Add
  // button, both land here.
  const addExpenseToGroup = (group) => {
    setActiveGroup({ id: group.id, name: group.name, employeeId: group.employeeId });
    setNextStepOpen(false);
    setOpenGroupId(null);
    setCreateDirty(false);
    setCreateOpen(true);
  };

  // Filing a loose expense into a batch, either an existing one or a new one
  // named in the dialog. A new batch is created for the expense's owner, not the
  // person moving it, so it can actually hold that expense.
  const moveToGroup = async ({ groupId, name }) => {
    if (!moveTarget) return;

    try {
      let targetGroupId = groupId;
      if (!targetGroupId && name) {
        const group = await createExpenseGroup.mutateAsync({
          name,
          employee_id: moveTarget.employee_id,
        });
        targetGroupId = group.id;
      }
      if (!targetGroupId) return;

      await moveExpenseToGroup.mutateAsync({ expenseId: moveTarget.id, groupId: targetGroupId });
      setMoveTarget(null);
      showExpenseToast('success', 'Expense moved into the group.');
    } catch (err) {
      showExpenseToast('error', String(err?.message || err || 'Unable to move this expense'));
    }
  };

  // The same move, driven from inside the group rather than from the expense:
  // pick something loose and pull it in.
  const addExistingExpenseToGroup = async (expense) => {
    if (!expense?.id || !openGroup?.id) return;

    try {
      await moveExpenseToGroup.mutateAsync({ expenseId: expense.id, groupId: openGroup.id });
      showExpenseToast('success', 'Expense added to the group.');
    } catch (err) {
      showExpenseToast('error', String(err?.message || err || 'Unable to add this expense'));
    }
  };

  const renameGroup = async (group, name) => {
    if (!group?.id || !name) return;

    try {
      await renameExpenseGroup.mutateAsync({ id: group.id, name });
      showExpenseToast('success', 'Group renamed.');
    } catch (err) {
      showExpenseToast('error', String(err?.message || err || 'Unable to rename this group'));
    }
  };

  const finishGroup = () => {
    setNextStepOpen(false);
    // Leave the group open on screen so submitting it is the obvious next step.
    if (activeGroup) setOpenGroupId(activeGroup.id);
    setActiveGroup(null);
    setGroupSavedCount(0);
  };

  const submitGroup = async (group) => {
    if (!group?.id) return;
    try {
      await submitExpenseGroup.mutateAsync({ id: group.id });
      showExpenseToast('success', `Submitted ${group.count} expense${group.count === 1 ? '' : 's'} for approval`);
      setOpenGroupId(null);
    } catch (err) {
      showExpenseToast('error', String(err?.message || err || 'Unable to submit the group'));
    }
  };

  // Only start the close animation here. Clearing editExpense resets the form
  // back to its default (unhidden) fields, so we defer that to onExited — once
  // the modal is no longer visible — to avoid the form visibly snapping while it
  // fades out.
  const requestCloseEdit = () => {
    if (editDirty && !shouldDiscardUnsavedChanges()) return;
    setEditOpen(false);
  };

  const forceCloseEdit = () => {
    setEditOpen(false);
  };

  const handleEditExited = () => {
    setEditExpense(null);
    setEditDirty(false);
  };

  // Changing which batch an expense belongs to is offered from the expense
  // itself. The edit modal closes first, so the group dialog takes its place
  // rather than stacking on it, and the expense is captured before that, since
  // closing clears it on the way out.
  const openMoveToGroup = () => {
    const target = editExpense;
    if (!target) return;
    if (editDirty && !shouldDiscardUnsavedChanges()) return;
    forceCloseEdit();
    setMoveTarget(target);
  };

  // Whoever filed an expense can delete it, not just its owner — filing one for
  // the wrong person is the mistake most in need of undoing, and the server
  // allows the same pair.
  const canManageExpense = (expense) => Boolean(currentEmployeeId)
    && (String(expense?.employee_id) === String(currentEmployeeId)
      || String(expense?.submitted_by || '') === String(currentEmployeeId));

  // The edit modal closes first — RSuite backdrops don't stack, so the
  // confirmation would open over a fully lit modal behind it. Unsaved edits are
  // dropped without asking: they are about to be deleted along with everything
  // else, so a "discard changes?" prompt would be asking about nothing.
  const deleteFromEdit = () => {
    const target = editExpense;
    forceCloseEdit();
    setDeleteExpenseTarget(target);
  };

  const confirmDeleteExpense = async () => {
    if (!deleteExpenseTarget?.id) return;
    await cancelExpense.mutateAsync(deleteExpenseTarget.id);
    setDeleteExpenseTarget(null);
  };

  const onEditExpense = (row) => {
    if (!row?.id) return;
    setEditExpense(row);
    setEditDirty(false);
    setEditOpen(true);
  };

  // Editing an expense that hasn't been submitted offers both: save the change
  // and leave it unsubmitted, or save it and send it. Saving is always the write
  // that happens first, so submitting can't claim an expense that still holds
  // the old figures.
  const submitEdit = async (payload, { asDraft = false } = {}) => {
    if (!editExpense?.id) return;
    await updateExpense.mutateAsync({ id: editExpense.id, ...payload });

    if (!asDraft && isDraftExpense(editExpense)) {
      await submitExpense.mutateAsync({ expenseId: editExpense.id });
    }

    // Keep editExpense until the modal has finished closing (handleEditExited)
    // so the form doesn't reset visibly during the fade-out.
    setEditOpen(false);
  };

  if (expensesQuery.isLoading) return <Placeholder.Paragraph rows={6} active />;

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Expenses' }]}
        icon="bi-receipt"
        title="My Expenses"
        actions={(
          <>
            <button type="button" className="bdc-btn" onClick={() => setHelpOpen(true)}>
              <i className="bi bi-question-circle" aria-hidden="true" />
              Help
            </button>

            <button type="button" className="bdc-btn bdc-btn--primary" onClick={startNewExpense}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New Expense
            </button>
          </>
        )}
      />

      {expensesQuery.error ? (
        <Message type="error" header="Error" showIcon>
          {String(expensesQuery.error?.message || expensesQuery.error)}
        </Message>
      ) : null}

      <ExpenseFilters expenses={allExpenses} value={filters} onChange={setFilters} />

      <Section
        title="Pending"
        count={pendingCount}
        collapsible
        defaultOpen
        aside={(
          <span className="bdc-section__note bdc-num">{formatCurrency(pendingTotal)}</span>
        )}
      >
        <ExpenseCardList
          expenses={pendingView.ungrouped}
          groups={pendingView.groups}
          onOpenGroup={(group) => setOpenGroupId(group.id)}
          onView={onEditExpense}
        />
      </Section>

      <Section
        title="Approved"
        count={approvedCount}
        collapsible
        aside={(
          <span className="bdc-section__note bdc-num">{formatCurrency(approvedTotal)}</span>
        )}
      >
        <ExpenseCardList
          expenses={approvedView.ungrouped}
          groups={approvedView.groups}
          onOpenGroup={(group) => setOpenGroupId(group.id)}
          showApprovalDetails
        />
      </Section>

      <Modal
        open={createOpen}
        onClose={requestCloseCreate}
        size={isMobileViewport ? 'full' : 'md'}
      >
        <Modal.Header>
          <Modal.Title>{createTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ExpenseForm
            onSubmit={submitCreate}
            onCancel={requestCloseCreate}
            onDirtyChange={setCreateDirty}
            // In a batch the owner was settled when the batch was started, and
            // the server rejects an expense whose owner doesn't match it.
            lockedEmployeeId={activeGroup?.employeeId || null}
            // In a batch nothing is sent for approval yet, so the action is a
            // save; the group is submitted as a whole later. Outside a batch
            // both are offered: save it for later, or send it now.
            showSaveAction={!activeGroup}
            // The All Hands meal is approved the moment it is filed, so it
            // can't sit in a batch waiting to be submitted with it.
            allowAllHandsMeal={!activeGroup}
            submitLabel={activeGroup ? 'Save Expense' : 'Submit Expense'}
            loading={createExpense.isPending || uploadExpenseAttachment.isPending}
            error={createExpense.error || uploadExpenseAttachment.error}
          />
        </Modal.Body>
      </Modal>

      <ExpenseHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        fullScreen={isMobileViewport}
      />

      <ExpenseTypeModal
        open={typeChooserOpen}
        onClose={() => setTypeChooserOpen(false)}
        onChooseIndividual={startIndividualExpense}
        onChooseGroup={startGroupedExpense}
        loading={createExpenseGroup.isPending}
      />

      <ExpenseMoveToGroupModal
        open={Boolean(moveTarget)}
        onClose={() => setMoveTarget(null)}
        expense={moveTarget}
        // Every batch on screen, not just the ones in the panel the expense
        // came from — where it can go doesn't depend on where it is now.
        groups={groupedView.groups}
        onMove={moveToGroup}
        loading={moveExpenseToGroup.isPending || createExpenseGroup.isPending}
      />

      <ExpenseGroupNextStepModal
        open={nextStepOpen}
        onClose={finishGroup}
        groupName={activeGroup?.name || ''}
        savedCount={groupSavedCount}
        onAddAnother={() => activeGroup && addExpenseToGroup(activeGroup)}
      />

      <ExpenseGroupModal
        open={Boolean(openGroup)}
        onClose={() => setOpenGroupId(null)}
        group={openGroup}
        // Close the group first, so the edit dialog takes its place rather
        // than stacking on it.
        onOpenExpense={(expense) => {
          setOpenGroupId(null);
          onEditExpense(expense);
        }}
        onAddExpense={addExpenseToGroup}
        onAddExistingExpense={addExistingExpenseToGroup}
        addableExpenses={addableExpenses}
        addingExisting={moveExpenseToGroup.isPending}
        onRenameGroup={renameGroup}
        renaming={renameExpenseGroup.isPending}
        onSubmitGroup={submitGroup}
        submitting={submitExpenseGroup.isPending}
        error={submitExpenseGroup.error}
      />

      <Modal open={editOpen} onClose={requestCloseEdit} onExited={handleEditExited} size="md">
        <Modal.Header>
          <Modal.Title>Edit Expense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {String(editExpense?.status || '').toLowerCase() === 'rejected' ? (
            <Message type="error" showIcon header="This expense was rejected" style={{ marginBottom: 16 }}>
              {editExpense?.approval_reason
                ? (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Reason</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{editExpense.approval_reason}</div>
                  </div>
                )
                : 'No reason was provided. Update the details and resubmit for review.'}
            </Message>
          ) : null}
          <ExpenseForm
            initialValue={editExpense}
            onSubmit={submitEdit}
            onCancel={requestCloseEdit}
            onDirtyChange={setEditDirty}
            submitRef={editSubmitRef}
            showActions={false}
            loading={updateExpense.isPending}
            error={updateExpense.error}
          />
        </Modal.Body>
        {/* Move and Delete sit on the left, because neither is one of the
            actions: one changes where the expense is filed, the other destroys it. */}
        <Modal.Footer
          left={isOpenExpense(editExpense) ? (
            <>
              <Button icon="collection" onClick={openMoveToGroup}>
                {editExpense?.group_id ? 'Move to another group' : 'Move into expense group'}
              </Button>
              {canManageExpense(editExpense) ? (
                <Button variant="danger" icon="trash" onClick={deleteFromEdit}>
                  Delete expense
                </Button>
              ) : null}
            </>
          ) : null}
        >
          <Button onClick={forceCloseEdit}>
            Cancel
          </Button>
          {/* An expense that hasn't been sent yet gets both actions: keep it
              unsubmitted, or send it. Everything else has only one thing
              left to do, so the primary button says what that is. */}
          {isDraftExpense(editExpense) ? (
            <Button
              loading={updateExpense.isPending}
              onClick={() => editSubmitRef.current?.save()}
            >
              Save for later
            </Button>
          ) : null}
          <Button
            variant="primary"
            loading={updateExpense.isPending || submitExpense.isPending}
            onClick={() => editSubmitRef.current?.submit()}
          >
            {isDraftExpense(editExpense)
              ? 'Submit Expense'
              : (String(editExpense?.status || '').toLowerCase() === 'rejected' ? 'Resubmit Expense' : 'Save Changes')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* A draft has never been seen by anyone, so deleting it is only that.
          A submitted one is sitting in somebody's approval queue, and deleting
          it takes it out of that queue as well — which is worth saying before
          they press the button, not after. */}
      <ConfirmModal
        open={!!deleteExpenseTarget}
        title={isDraftExpense(deleteExpenseTarget) ? 'Delete Expense' : 'Withdraw and Delete Expense'}
        body={isDraftExpense(deleteExpenseTarget) ? (
          'Are you sure you would like to delete this expense?'
        ) : (
          <>
            This expense has been submitted and is waiting on approval. Deleting it will
            cancel that approval request and remove the expense. This cannot be undone.
          </>
        )}
        confirmText={isDraftExpense(deleteExpenseTarget) ? 'Delete' : 'Cancel Approval and Delete'}
        danger
        loading={cancelExpense.isPending}
        onConfirm={confirmDeleteExpense}
        onCancel={() => setDeleteExpenseTarget(null)}
      />
    </div>
  );
}
